import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, DownloadIcon, FileSpreadsheetIcon, BriefcaseIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Service } from '../../types';
import { formatCurrency } from '../../utils/helpers';
import { exportServicesToExcel } from '../../utils/excelExport';
export function Services() {
  const {
    services,
    addService,
    updateService,
    deleteService
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('services');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    duration: 30,
    price: 0,
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const filteredServices = services.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const openNewForm = () => {
    if (!canCreate('services')) return;
    setSelectedService(null);
    setFormData({
      name: '',
      duration: 30,
      price: 0,
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (service: Service) => {
    if (!canEdit('services')) return;
    setSelectedService(service);
    setFormData({
      name: service.name,
      duration: service.duration,
      price: service.price,
      description: service.description
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (service: Service) => {
    if (!canDelete('services')) return;
    setSelectedService(service);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (formData.duration <= 0) errors.duration = 'La duración debe ser mayor a 0';
    if (formData.price < 0) errors.price = 'El precio no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedService) {
      updateService(selectedService.id, formData);
    } else {
      addService(formData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('services')) return;
    if (selectedService) {
      deleteService(selectedService.id);
      setIsDeleteConfirmOpen(false);
    }
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToExcel(services);
    setIsExporting(false);
  };
  return <div className="space-y-6" data-id="element-1479">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1480">
        <div data-id="element-1481">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1482">
            Servicios
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1483">
            Gestión de servicios médicos
            {readOnly && <span className="ml-2 text-amber-600 dark:text-amber-400" data-id="element-1484">
                (Solo lectura)
              </span>}
          </p>
        </div>
        {canCreate('services') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1486" />} onClick={openNewForm} data-id="element-1485">
            Nuevo Servicio
          </Button>}
      </div>

      {/* Beautiful Export Card */}
      <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30 border-orange-200 dark:border-orange-800" data-id="element-1487">
        <div className="flex items-center justify-between" data-id="element-1488">
          <div className="flex items-center gap-4" data-id="element-1489">
            <div className="w-14 h-14 rounded-xl bg-orange-500 dark:bg-orange-600 flex items-center justify-center shadow-lg shadow-orange-500/20" data-id="element-1490">
              <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-1491" />
            </div>
            <div data-id="element-1492">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1493">
                Exportar Base de Datos
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1494">
                Descarga todos los servicios en formato Excel
              </p>
              <div className="flex items-center gap-4 mt-2" data-id="element-1495">
                <div className="flex items-center gap-1.5 text-sm text-orange-700 dark:text-orange-400" data-id="element-1496">
                  <BriefcaseIcon className="w-4 h-4" data-id="element-1497" />
                  <span className="font-medium" data-id="element-1498">
                    {services.length} servicios
                  </span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1499">
                  • Incluye: Nombre, Duración, Precio, Descripción
                </div>
              </div>
            </div>
          </div>
          <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1501" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-orange-500/20 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-200" data-id="element-1500">
            {isExporting ? 'Exportando...' : 'Descargar Excel'}
          </Button>
        </div>
      </Card>

      {/* Search */}
      <Card data-id="element-1502">
        <div className="relative" data-id="element-1503">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1504" />
          <Input placeholder="Buscar servicios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1505" />
        </div>
      </Card>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-1506">
          <LockIcon className="w-4 h-4" data-id="element-1507" />
          <span data-id="element-1508">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            servicios.
          </span>
        </div>}

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1509">
        {filteredServices.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-1510">
            No se encontraron servicios
          </div> : filteredServices.map(service => <Card key={service.id} data-id="element-1511">
              <div className="flex items-start justify-between mb-4" data-id="element-1512">
                <div data-id="element-1513">
                  <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1514">
                    {service.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1515">
                    {service.duration} minutos
                  </p>
                </div>
                {!readOnly && <div className="flex gap-1" data-id="element-1516">
                    {canEdit('services') && <button onClick={() => openEditForm(service)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar servicio" data-id="element-1517">
                        <EditIcon className="w-4 h-4" data-id="element-1518" />
                      </button>}
                    {canDelete('services') && <button onClick={() => openDeleteConfirm(service)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar servicio" data-id="element-1519">
                        <TrashIcon className="w-4 h-4" data-id="element-1520" />
                      </button>}
                  </div>}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-1521">
                {service.description || 'Sin descripción'}
              </p>
              <p className="text-xl font-bold text-sky-600 dark:text-sky-400" data-id="element-1522">
                {formatCurrency(service.price)}
              </p>
            </Card>)}
      </div>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedService ? 'Editar Servicio' : 'Nuevo Servicio'} size="md" data-id="element-1523">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1524">
          <Input label="Nombre del Servicio" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} error={formErrors.name} data-id="element-1525" />

          <div className="grid grid-cols-2 gap-4" data-id="element-1526">
            <Input type="number" label="Duración (minutos)" value={formData.duration} onChange={e => setFormData({
            ...formData,
            duration: parseInt(e.target.value) || 0
          })} error={formErrors.duration} min={1} data-id="element-1527" />
            <Input type="number" label="Precio (DOP)" value={formData.price} onChange={e => setFormData({
            ...formData,
            price: parseFloat(e.target.value) || 0
          })} error={formErrors.price} min={0} data-id="element-1528" />
          </div>

          <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
          ...formData,
          description: e.target.value
        })} rows={3} data-id="element-1529" />

          <div className="flex justify-end gap-3 pt-4" data-id="element-1530">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1531">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1532">
              {selectedService ? 'Guardar Cambios' : 'Crear Servicio'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1533">
        <div className="space-y-4" data-id="element-1534">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1535">
            ¿Está seguro de que desea eliminar el servicio "
            {selectedService?.name}"?
          </p>
          <div className="flex justify-end gap-3" data-id="element-1536">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1537">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1538">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}